// ============================================================================
// API Response Validation Schemas
// Feature: 101-codebase-audit-refactor (T086)
// Created: 2026-01-15
// Description: Zod schemas for runtime validation of API responses
// ============================================================================

import { z } from 'zod';

// ============================================================================
// Base Response Schemas
// ============================================================================

/**
 * Base API response wrapper schema
 */
export const apiResponseSchema = <T extends z.ZodTypeAny>(dataSchema: T) =>
  z.object({
    success: z.boolean(),
    data: dataSchema,
    error: z.string().optional(),
    code: z.string().optional(),
  });

/**
 * Paginated API response schema
 */
export const paginatedResponseSchema = <T extends z.ZodTypeAny>(itemSchema: T) =>
  z.object({
    success: z.boolean(),
    data: z.array(itemSchema),
    pagination: z.object({
      page: z.number(),
      pageSize: z.number(),
      total: z.number(),
      totalPages: z.number(),
      hasNextPage: z.boolean(),
      hasPreviousPage: z.boolean(),
    }),
    error: z.string().optional(),
  });

// ============================================================================
// Common Field Schemas
// ============================================================================

export const uuidSchema = z.guid();
export const dateStringSchema = z.string().datetime().or(z.string().regex(/^\d{4}-\d{2}-\d{2}/));
export const nullableDateSchema = dateStringSchema.nullable();

// ============================================================================
// SLA Schemas
// ============================================================================

export const slaTierSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  description: z.string().nullable().optional(),
  priority: z.number(),
  uptime_target: z.number(),
  max_response_time_ms: z.number(),
  max_recovery_time_hours: z.number(),
  backup_frequency_hours: z.number(),
  retention_days: z.number(),
  support_level: z.string(),
  is_deprecated: z.boolean().optional(),
  created_at: dateStringSchema.optional(),
  updated_at: dateStringSchema.optional(),
});

export const clientSlaAssignmentSchema = z.object({
  id: uuidSchema,
  client_id: uuidSchema,
  sla_tier_id: uuidSchema,
  effective_from: dateStringSchema,
  effective_to: nullableDateSchema,
  assigned_by: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  created_at: dateStringSchema.optional(),
});

export const clientSlaAssignmentWithTierSchema = clientSlaAssignmentSchema.extend({
  tier_name: z.string().optional(),
  tier_priority: z.number().optional(),
});

export const clientSlaOverrideSchema = z.object({
  id: uuidSchema,
  client_id: uuidSchema,
  override_type: z.string(),
  override_value: z.string(),
  reason: z.string(),
  approved_by: z.string().nullable().optional(),
  effective_from: dateStringSchema,
  effective_to: nullableDateSchema,
  is_active: z.boolean(),
  created_at: dateStringSchema.optional(),
  updated_at: dateStringSchema.optional(),
});

export const maintenanceWindowSchema = z.object({
  id: uuidSchema,
  client_id: uuidSchema.nullable().optional(),
  capacity_id: uuidSchema.nullable().optional(),
  name: z.string(),
  description: z.string().nullable().optional(),
  start_time: dateStringSchema,
  end_time: dateStringSchema,
  recurrence_pattern: z.string().nullable().optional(),
  is_active: z.boolean(),
  sla_exclusion: z.boolean(),
  created_by: z.string().nullable().optional(),
  created_at: dateStringSchema.optional(),
  updated_at: dateStringSchema.optional(),
});

export const effectiveSlaSchema = z.object({
  capacity_id: uuidSchema,
  capacity_name: z.string(),
  capacity_type: z.string(),
  client_id: uuidSchema.nullable().optional(),
  client_name: z.string().nullable().optional(),
  tier_id: uuidSchema,
  tier_name: z.string(),
  tier_priority: z.number(),
  uptime_target: z.number(),
  max_response_time_ms: z.number(),
  max_recovery_time_hours: z.number(),
  backup_frequency_hours: z.number(),
  retention_days: z.number(),
  support_level: z.string(),
  has_override: z.boolean().optional(),
  override_details: z.record(z.string(), z.unknown()).nullable().optional(),
  in_maintenance: z.boolean().optional(),
  maintenance_window: z.string().nullable().optional(),
});

// Code Quality schemas retired (Spec 216 T036 — Feature 099 removed).

// ============================================================================
// Performance Schemas
// ============================================================================

export const performanceBenchmarkSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  description: z.string().nullable().optional(),
  metric_type: z.string(),
  target_value: z.number(),
  warning_threshold: z.number().nullable().optional(),
  critical_threshold: z.number().nullable().optional(),
  unit: z.string(),
  is_active: z.boolean(),
  created_at: dateStringSchema.optional(),
  updated_at: dateStringSchema.optional(),
});

export const performanceMeasurementSchema = z.object({
  id: uuidSchema,
  benchmark_id: uuidSchema,
  capacity_id: uuidSchema.nullable().optional(),
  measured_value: z.number(),
  status: z.string(),
  measured_at: dateStringSchema,
  notes: z.string().nullable().optional(),
});

// ============================================================================
// Architecture Schemas
// ============================================================================

export const architectureAssessmentSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  description: z.string().nullable().optional(),
  module_name: z.string(),
  assessment_type: z.string(),
  status: z.string(),
  score: z.number().nullable().optional(),
  findings: z.array(z.record(z.string(), z.unknown())).nullable().optional(),
  recommendations: z.array(z.string()).nullable().optional(),
  assessed_by: z.string().nullable().optional(),
  assessed_at: dateStringSchema.optional(),
  created_at: dateStringSchema.optional(),
  updated_at: dateStringSchema.optional(),
});

// ============================================================================
// Data Retention Schemas
// ============================================================================

export const dataRetentionPolicySchema = z.object({
  id: uuidSchema,
  name: z.string(),
  table_name: z.string(),
  retention_days: z.number(),
  archive_strategy: z.string().nullable().optional(),
  is_active: z.boolean(),
  last_execution_at: nullableDateSchema.optional(),
  next_execution_at: nullableDateSchema.optional(),
  created_at: dateStringSchema.optional(),
  updated_at: dateStringSchema.optional(),
});

export const retentionExecutionLogSchema = z.object({
  id: uuidSchema,
  policy_id: uuidSchema,
  started_at: dateStringSchema,
  completed_at: nullableDateSchema,
  status: z.string(),
  rows_processed: z.number(),
  rows_deleted: z.number(),
  rows_archived: z.number(),
  error_message: z.string().nullable().optional(),
});

// ============================================================================
// Validation Helper
// ============================================================================

/**
 * Safely validate API response data with Zod schema.
 * Logs validation errors but doesn't throw - returns data as-is on failure.
 * This allows graceful degradation when backend returns unexpected shapes.
 * 
 * The function preserves the original data type T by using passthrough validation.
 * Schemas are used only for shape checking, not type transformation.
 */
export function validateResponse<T>(
  schema: z.ZodTypeAny,
  data: T,
  context?: string
): T {
  const result = schema.safeParse(data);
  
  if (!result.success) {
    console.warn(
      `[API Validation] ${context || 'Response'} validation failed:`,
      result.error.format()
    );
  }
  
  // Always return original data to preserve TypeScript types
  return data;
}

/**
 * Strict validation that throws on failure.
 * Use when data integrity is critical.
 */
export function validateResponseStrict<T>(
  schema: z.ZodTypeAny,
  data: T,
  context?: string
): T {
  const result = schema.safeParse(data);
  
  if (!result.success) {
    const errorMessage = `[API Validation] ${context || 'Response'} validation failed`;
    console.error(errorMessage, result.error.format());
    throw new Error(`${errorMessage}: ${result.error.message}`);
  }
  
  // Return original data to preserve TypeScript types
  return data;
}
